import React, { useState } from 'react';
import { updateSettings } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Link2, Plus, Save, X } from 'lucide-react';
import { toast } from 'sonner';

export const GeneralTab = ({ settings, setSettings }) => {
    const [saving, setSaving] = useState(false);
    const [newType, setNewType] = useState('');
    const [newSource, setNewSource] = useState('');

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateSettings({
                google_review_link: settings.google_review_link,
                types_travaux: settings.types_travaux,
                sources_lead: settings.sources_lead,
                raisons_refus: settings.raisons_refus,
                bonus_tiers: settings.bonus_tiers
            });
            toast.success('Paramètres enregistrés');
        } catch {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleAddType = () => {
        if (!newType.trim()) return;
        setSettings((prev) => ({ ...prev, types_travaux: [...(prev.types_travaux || []), newType.trim()] }));
        setNewType('');
    };

    const handleRemoveType = (index) => {
        setSettings((prev) => ({ ...prev, types_travaux: prev.types_travaux.filter((_, i) => i !== index) }));
    };

    const handleAddSource = () => {
        if (!newSource.trim()) return;
        setSettings((prev) => ({ ...prev, sources_lead: [...(prev.sources_lead || []), newSource.trim()] }));
        setNewSource('');
    };

    const handleRemoveSource = (index) => {
        setSettings((prev) => ({ ...prev, sources_lead: prev.sources_lead.filter((_, i) => i !== index) }));
    };

    return (
        <div className="space-y-6">
            <div className="bg-white border border-[#E4E4E7] p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Link2 className="w-5 h-5 text-[#E85A24]" />
                    <h3 className="font-semibold text-[#0A0A0A]">Lien Avis Google</h3>
                </div>
                <div className="space-y-2">
                    <Label className="field-label">URL de la page Google Reviews</Label>
                    <Input
                        value={settings.google_review_link || ''}
                        onChange={(e) => setSettings((prev) => ({ ...prev, google_review_link: e.target.value }))}
                        placeholder="https://g.page/r/..."
                        className="h-11 rounded-sm"
                        data-testid="google-review-input"
                    />
                    <p className="text-sm text-[#52525B]">Ce lien sera envoyé aux clients pour qu'ils puissent laisser un avis.</p>
                </div>
            </div>

            <div className="bg-white border border-[#E4E4E7] p-6">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">Types de travaux</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                    {settings.types_travaux?.map((type, index) => (
                        <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-[#F4F4F5] text-sm">
                            {type}
                            <button onClick={() => handleRemoveType(index)} className="ml-1 text-[#52525B] hover:text-[#DC2626]">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2">
                    <Input
                        value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                        placeholder="Nouveau type de travaux"
                        className="h-10 rounded-sm"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddType()}
                    />
                    <Button onClick={handleAddType} variant="outline" className="rounded-sm">
                        <Plus className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-[#E4E4E7] p-6">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">Sources de leads</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                    {settings.sources_lead?.map((source, index) => (
                        <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-[#F4F4F5] text-sm">
                            {source}
                            <button onClick={() => handleRemoveSource(index)} className="ml-1 text-[#52525B] hover:text-[#DC2626]">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2">
                    <Input
                        value={newSource}
                        onChange={(e) => setNewSource(e.target.value)}
                        placeholder="Nouvelle source"
                        className="h-10 rounded-sm"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSource()}
                    />
                    <Button onClick={handleAddSource} variant="outline" className="rounded-sm">
                        <Plus className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-[#E4E4E7] p-6">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">Seuils "En retard"</h3>
                <p className="text-xs text-[#52525B] mb-4">Définissez à partir de combien de jours une action est considérée en retard.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="field-label">Devis sans retour (jours)</Label>
                        <Input
                            type="number"
                            min="1"
                            value={settings.overdue_thresholds?.devis_sans_retour_days ?? 7}
                            onChange={(e) => setSettings((prev) => ({
                                ...prev,
                                overdue_thresholds: {
                                    ...(prev.overdue_thresholds || {}),
                                    devis_sans_retour_days: Number(e.target.value) || 7
                                }
                            }))}
                            className="h-10 rounded-sm mt-1"
                            data-testid="threshold-devis-input"
                        />
                        <p className="text-xs text-[#52525B] mt-1">Défaut : 7 jours</p>
                    </div>
                    <div>
                        <Label className="field-label">Client silencieux (jours)</Label>
                        <Input
                            type="number"
                            min="7"
                            value={settings.overdue_thresholds?.client_silencieux_days ?? 90}
                            onChange={(e) => setSettings((prev) => ({
                                ...prev,
                                overdue_thresholds: {
                                    ...(prev.overdue_thresholds || {}),
                                    client_silencieux_days: Number(e.target.value) || 90
                                }
                            }))}
                            className="h-10 rounded-sm mt-1"
                            data-testid="threshold-silence-input"
                        />
                        <p className="text-xs text-[#52525B] mt-1">Défaut : 90 jours</p>
                    </div>
                </div>
            </div>

            <div className="flex justify-end">
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                    data-testid="save-settings-btn"
                >
                    <Save className="w-4 h-4 mr-2" /> Enregistrer
                </Button>
            </div>
        </div>
    );
};
